import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Loader2, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import apiClient from '../api/client';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post('/auth/forgot-password', { email });

      if (response.data.success) {
        setSuccess(true);
        // The token is returned for password reset
        if (response.data.resetToken) {
          setResetToken(response.data.resetToken);
        }
        toast.success('Password reset instructions sent');
      } else {
        toast.error(response.data.error || 'Failed to request password reset');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.error || 'Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-black via-primary-charcoal to-primary-slate flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-accent-gold mb-2">Tile BMS</h1>
          <p className="text-gray-300">Password Reset</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          {success ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-primary-black mb-4">Check Your Email</h2>
              <p className="text-secondary-gray mb-6">
                If an account exists with that email, you'll receive password reset instructions.
              </p>

              {/* Development only - show reset link */}
              {resetToken && (
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                  <p className="text-xs font-semibold text-yellow-800 uppercase mb-2">
                    Development Mode
                  </p>
                  <p className="text-sm text-yellow-700 mb-2">
                    Reset link (would be sent via email in production):
                  </p>
                  <a
                    href={`/reset-password?token=${resetToken}`}
                    className="text-sm text-accent-gold hover:underline break-all"
                  >
                    Click here to reset password
                  </a>
                </div>
              )}

              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-accent-gold hover:text-accent-goldDark transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-primary-black mb-2 text-center">
                Forgot Password?
              </h2>
              <p className="text-secondary-gray text-center mb-6">
                Enter your email address and we'll send you instructions to reset your password.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent transition-colors"
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <span>Send Reset Instructions</span>
                  )}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm text-accent-gold hover:text-accent-goldDark transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          © 2025 Eldaved Global. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
